/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { last, uniqBy } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AssignRequestsMutationVariables,
  AssignmentPolicyType,
  DepartmentUser,
  ListQuestionsByDepartmentQuery,
  ListQuestionsByDepartmentQueryVariables,
  ProcedureStage,
  Request,
  RequestQuestion,
  RequestQuestionValueCounts,
  SortOrder,
  useAssignRequestsMutation,
  useGetExceptionRequestLazyQuery,
  useGetExceptionsStatusCountsLazyQuery,
  useGetRequestQuestionLazyQuery,
  useListDepartmentCollaboratorsQuery,
  useListQuestionsByDepartmentQuery
} from '@/generated/graphql';
import { useAppSelector } from '@/stores/hooks';
import { departmentQuerySelector } from '@/stores/slices/department';
import {
  prepareException,
  prepareExceptionStatusCounts
} from '@/transformers/exception';
import { userSelector } from '@/stores/slices/user';
import routes from '@/utils/routes';

import { useURLFilters } from '@/hooks/useURLFilters';
import { prepareExceptionFiltersForRequest } from '@/utils/exception';
import { NetworkStatus } from '@apollo/client';
import useALCFlag from '@/hooks/useALCFlag';
import useMultiSelectCheckboxes from '@/hooks/useMultiSelectCheckboxes';
import {
  ExceptionStateType,
  IException,
  IExceptionFilterOption,
  IExceptionFilterOptions,
  IExceptionSelectedFilters,
  IExceptionStatusCounts,
  IExceptionsCentreContext,
  IExceptionsCentreContextMethods
} from './interface';

const DEFAULT_PAGE_SIZE = 20;

const ExceptionsCentreContext = React.createContext<IExceptionsCentreContext>(
  null!
);

interface IExceptionsCentreProviderProps {
  children: React.ReactNode;
}

export function ExceptionsCentreProvider(
  props: IExceptionsCentreProviderProps
) {
  const { children } = props;

  const { isALCEnabled } = useALCFlag();

  const { username } = useSelector(userSelector);
  const { department } = useAppSelector(departmentQuerySelector);
  const navigate = useNavigate();
  const params = useParams();

  const filterConfig: IExceptionSelectedFilters = useMemo(
    () => ({
      process: [],
      state: [ExceptionStateType.PENDING],
      dateRange: [], // [before, after],
      assignee: username ? [username] : [],
      typename: [],
      searchText: []
    }),
    [username]
  );

  // exceptions filter available options
  const [filterOptions, setFilterOptions] = useState<IExceptionFilterOptions>({
    process: []
  });

  const [selectedFilters, setSelectedFilters] =
    useURLFilters<IExceptionSelectedFilters>(filterConfig);

  // exceptions data (paginated)
  const [exceptions, setExceptions] = useState<IException[]>([]);

  // exceptions count
  const [exceptionsCount, setExceptionsCount] = useState<number>(0);

  const {
    selections: selectedExceptions,
    handleCheckboxClick,
    resetSelections
  } = useMultiSelectCheckboxes<IException>(exceptions);

  // active exception (for single actions)
  const [activeException, setActiveException] = useState<IException | null>(
    null
  );

  // pagination page number
  const [page, setPage] = useState<number>(1);

  // urlActiveExceptionId
  // This is a state because when we clear, we do router.navigate instead of window.navigate
  const [urlActiveExceptionId, setUrlActiveExceptionId] = useState<
    string | null
  >(params.exceptionId || null);

  // status counts
  const [statusCounts, setStatusCounts] = useState<IExceptionStatusCounts>({
    pending: 0,
    archived: 0,
    handled: 0,
    total: 0
  });

  // active exception request query
  const [fetchActiveExceptionRequest, { loading: activeExceptionLoading }] =
    useGetExceptionRequestLazyQuery({
      onCompleted(data) {
        const request = data.getRequest as Request | null;
        if (request) {
          setActiveException((prevException) => ({
            ...prevException!,
            request
          }));
        }
      },
      onError: () => {
        message.error(
          'Failed to fetch the current exception! Please try again.'
        );
      }
    });

  const activateException: IExceptionsCentreContextMethods['activateException'] =
    useCallback(
      (exception) => {
        if (exception === null) {
          setActiveException(null);
          return;
        }

        setActiveException(exception);
        if (activeException?.id === exception.id) {
          fetchActiveExceptionRequest({
            variables: {
              id: exception.requestId
            }
          });
        }
      },
      [activeException, fetchActiveExceptionRequest]
    );

  const scrollExceptionIntoView = (exception: IException) => {
    const exceptionElement = document.getElementById(
      `exception-${exception.id}`
    );
    if (exceptionElement) {
      exceptionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const setExceptionsFromData = useCallback(
    (data: ListQuestionsByDepartmentQuery) => {
      const fetchedQuestions = (data?.listQuestionsByDepartment?.items ||
        []) as RequestQuestion[];
      const preparedExceptions = fetchedQuestions?.map(prepareException) || [];

      setExceptions(preparedExceptions);
      setExceptionsCount(data.listQuestionsByDepartment?.total || 0);

      if (preparedExceptions.length > 0) {
        if (page === 1) {
          activateException(preparedExceptions[0]);
          scrollExceptionIntoView(preparedExceptions[0]);
        }
      } else {
        activateException(null);
      }

      const valueCounts = data?.listQuestionsByDepartment?.valueCounts;
      const options: IExceptionFilterOptions = {
        process: (valueCounts?.procedures || []) as IExceptionFilterOption[]
      };
      setFilterOptions(options);
      setStatusCounts(prepareExceptionStatusCounts(valueCounts));
    },
    [activateException, page]
  );

  const exceptionRequestFilters: ListQuestionsByDepartmentQueryVariables['input']['filters'] =
    useMemo(
      () => prepareExceptionFiltersForRequest(selectedFilters),
      [selectedFilters]
    );

  const { loading, error, fetchMore, refetch, networkStatus } =
    useListQuestionsByDepartmentQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: !department.id || !!urlActiveExceptionId,
      variables: {
        input: {
          departmentId: department.id,
          sortField: ['request', 'createdAt'],
          sortOrder: SortOrder.Descend,
          filters: {
            ...exceptionRequestFilters,
            ...(isALCEnabled ? { stage: ProcedureStage.Published } : {})
          },
          currentPage: 1,
          pageSize: DEFAULT_PAGE_SIZE
        }
      },
      onCompleted(data) {
        // data is the whole data in the cache. It gets updated by fetchMore as well
        const dataLength = data.listQuestionsByDepartment?.items?.length || 0;
        if (dataLength <= DEFAULT_PAGE_SIZE) {
          setPage(1);
        }
        setExceptionsFromData(data);
      }
    });

  // used to fetch active exception if present in url search params
  const [fetchUrlActiveException] = useGetRequestQuestionLazyQuery({
    onCompleted(data) {
      const question = data.getRequestQuestion as RequestQuestion | null;
      if (question) {
        const exception = prepareException(question) as any;
        const uniqueExceptions = uniqBy([exception, ...exceptions], 'id');
        setExceptions(uniqueExceptions);
        setActiveException(exception);
      }
    }
  });

  // department collaborators query
  const { data: departmentCollaboratorsData } =
    useListDepartmentCollaboratorsQuery({
      variables: {
        departmentId: department.id
      },
      skip: !department?.id
    });

  // assign requests mutation
  const [assignRequestsMutation] = useAssignRequestsMutation();

  // status counts query (used to refresh once an exception is handled)
  const [fetchExceptionsStatusCounts] = useGetExceptionsStatusCountsLazyQuery({
    onCompleted(data) {
      const valueCounts = (data?.listQuestionsByDepartment as any)
        ?.valueCounts as RequestQuestionValueCounts | undefined;
      setStatusCounts(prepareExceptionStatusCounts(valueCounts));
    }
  });

  const departmentUsers = useMemo(
    () =>
      (departmentCollaboratorsData?.listDepartmentCollaborators?.items ||
        []) as DepartmentUser[],
    [departmentCollaboratorsData]
  );

  const refreshStatusCounts = useCallback(() => {
    fetchExceptionsStatusCounts({
      variables: {
        input: {
          departmentId: department.id,
          filters: {
            state: [ExceptionStateType.PENDING],
            requestAssignee: [username!],
            ...(isALCEnabled ? { stage: ProcedureStage.Published } : {})
          }
        }
      }
    });
  }, [department.id, fetchExceptionsStatusCounts, username, isALCEnabled]);

  const updateSelectedFilters = useCallback(
    (filters: IExceptionSelectedFilters | {}, forceClear: boolean = false) => {
      setPage(1);
      setSelectedFilters(filters, forceClear);
    },
    [setSelectedFilters]
  );

  const resetExceptions = useCallback(() => {
    updateSelectedFilters({});
  }, [updateSelectedFilters]);

  useEffect(() => {
    if (department?.id && urlActiveExceptionId) {
      updateSelectedFilters({});
      fetchUrlActiveException({
        variables: {
          id: urlActiveExceptionId
        }
      });
    }
  }, [
    urlActiveExceptionId,
    department?.id,
    updateSelectedFilters,
    fetchUrlActiveException
  ]);

  useEffect(() => {
    if (activeException?.id) {
      fetchActiveExceptionRequest({
        variables: {
          id: activeException.requestId
        }
      });
    }
  }, [
    activeException?.id,
    activeException?.requestId,
    fetchActiveExceptionRequest
  ]);

  const fetchNextPageExceptions: IExceptionsCentreContextMethods['fetchNextPageExceptions'] =
    useCallback(async () => {
      const newPage = page + 1;
      setPage(newPage);
      await fetchMore({
        variables: {
          input: {
            departmentId: department.id,
            sortField: ['request', 'createdAt'],
            sortOrder: SortOrder.Descend,
            filters: exceptionRequestFilters,
            currentPage: newPage,
            pageSize: DEFAULT_PAGE_SIZE
          }
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const newList = uniqBy(
            [
              ...(prev.listQuestionsByDepartment?.items || []),
              ...(fetchMoreResult.listQuestionsByDepartment?.items || [])
            ],
            'id'
          );

          return {
            ...prev,
            listQuestionsByDepartment: {
              ...prev.listQuestionsByDepartment,
              ...fetchMoreResult.listQuestionsByDepartment,
              items: newList
            }
          };
        }
      });
    }, [page, department.id, exceptionRequestFilters, fetchMore]);

  const clearUrlActiveExceptionId: IExceptionsCentreContextMethods['clearUrlActiveExceptionId'] =
    useCallback(() => {
      setUrlActiveExceptionId(null);
      navigate(routes.exceptions.getExceptionsPageUrl(department!.id));
    }, [navigate, department]);

  const selectException: IExceptionsCentreContextMethods['selectException'] =
    handleCheckboxClick;

  const updateSelectedFilter: IExceptionsCentreContextMethods['updateSelectedFilter'] =
    useCallback(
      (key, value, reset) => {
        if (reset) {
          setPage(1);
          updateSelectedFilters({
            ...selectedFilters,
            [key]: value
          });
          return;
        }

        const currentFilters = selectedFilters[key];
        const isSelected = currentFilters?.find((filter) => filter === value);
        updateSelectedFilters({
          ...selectedFilters,
          [key]: isSelected
            ? currentFilters.filter((filter) => filter !== value)
            : [...currentFilters, value]
        });
      },
      [selectedFilters, updateSelectedFilters]
    );

  const isFilterApplied = Object.values(selectedFilters).some(
    (filter) => filter?.length > 0
  );

  const gotoNextException: IExceptionsCentreContextMethods['gotoNextException'] =
    useCallback(
      (currentException, refetchIfNotFound) => {
        const activeExceptionIndex = exceptions.findIndex(
          (exception) => exception.id === currentException.id
        );
        const nextException = exceptions[activeExceptionIndex + 1];
        if (nextException) {
          activateException(nextException);
          scrollExceptionIntoView(nextException);
        } else {
          if (refetchIfNotFound) {
            // if user has reached the end of pending exceptions, then refetch
            refetch();
          } else {
            activateException(null);
            message.info('No more exceptions to show');
          }
        }
      },
      [exceptions, activateException, refetch]
    );

  const gotoPreviousException: IExceptionsCentreContextMethods['gotoPreviousException'] =
    useCallback(
      (currentException) => {
        const activeExceptionIndex = exceptions.findIndex(
          (exception) => exception.id === currentException.id
        );
        const previousException = exceptions[activeExceptionIndex - 1];
        if (previousException) {
          activateException(previousException);
          scrollExceptionIntoView(previousException);
        }
      },
      [exceptions, activateException]
    );

  const assignExceptions: IExceptionsCentreContextMethods['assignExceptions'] =
    useCallback(
      (exceptionsToAssign, collaborator) => {
        const variables: AssignRequestsMutationVariables = {
          input: {
            requestIds: exceptionsToAssign.map(
              (exception) => exception.requestId
            ),
            assignmentPolicy: {
              policyType: AssignmentPolicyType.Collaborator,
              collaborator
            }
          }
        };

        return assignRequestsMutation({
          variables
        }).then((resp) => {
          message.success('Assigned successfully');

          resetSelections();

          const updatedExceptions = exceptions.map((exception) => {
            if (exceptionsToAssign.some((e) => e.id === exception.id)) {
              return {
                ...exception,
                assignee: collaborator
              };
            }
            return exception;
          });
          setExceptions(updatedExceptions);
          refreshStatusCounts();

          const lastException = last(exceptionsToAssign);
          if (lastException) {
            gotoNextException(lastException, true);
          }

          return resp;
        });
      },
      [
        assignRequestsMutation,
        resetSelections,
        exceptions,
        refreshStatusCounts,
        gotoNextException
      ]
    );

  const markExceptionsAsHandled: IExceptionsCentreContextMethods['markExceptionsAsHandled'] =
    useCallback(
      (exceptionsToMarkAsHandled, actionType) => {
        const updatedExceptions = exceptions.map((exception) => {
          if (exceptionsToMarkAsHandled.some((e) => e.id === exception.id)) {
            return {
              ...exception,
              state: actionType
            };
          }
          return exception;
        });

        setExceptions(updatedExceptions);
        refreshStatusCounts();

        const lastException = last(exceptionsToMarkAsHandled);
        if (lastException) {
          gotoNextException(lastException, true);
        }
      },
      [exceptions, refreshStatusCounts, gotoNextException]
    );

  const value: IExceptionsCentreContext = useMemo(
    () => ({
      loading: loading && networkStatus !== NetworkStatus.fetchMore,
      error,
      exceptions,
      totalExceptionsCount: exceptionsCount,
      exceptionsFilterOptions: filterOptions,
      selectedFilters,
      selectedExceptions,
      activeException,
      activeExceptionLoading,
      departmentUsers,
      isFilterApplied,
      statusCounts,
      urlActiveExceptionId,

      methods: {
        resetExceptions,
        fetchNextPageExceptions,
        selectException,
        clearSelectedExceptions: () => {
          resetSelections();
        },
        activateException,
        updateSelectedFilter,
        clearSelectedFilters: () => {
          updateSelectedFilters({}, true);
        },
        gotoNextException,
        gotoPreviousException,
        assignExceptions,
        markExceptionsAsHandled,
        clearUrlActiveExceptionId
      }
    }),
    [
      loading,
      networkStatus,
      error,
      exceptions,
      exceptionsCount,
      filterOptions,
      selectedFilters,
      selectedExceptions,
      activeException,
      departmentUsers,
      isFilterApplied,
      statusCounts,
      urlActiveExceptionId,
      activeExceptionLoading,
      resetExceptions,
      fetchNextPageExceptions,
      selectException,
      activateException,
      updateSelectedFilter,
      gotoNextException,
      gotoPreviousException,
      assignExceptions,
      markExceptionsAsHandled,
      clearUrlActiveExceptionId,
      resetSelections,
      updateSelectedFilters
    ]
  );

  return (
    <ExceptionsCentreContext.Provider value={value}>
      {children}
    </ExceptionsCentreContext.Provider>
  );
}

export function useExceptionsCentreContext() {
  return React.useContext(ExceptionsCentreContext);
}
